export interface RefinementAxis {
  key: string
  label: string
  options: string[]
}

export type Refinements = Record<string, string>

interface RefinementChipsProps {
  axes: RefinementAxis[]
  active: Refinements
  onSelect: (axisKey: string, option: string) => void
  onClear: (axisKey: string) => void
  disabled?: boolean
}

// Renders the active refinements as removable pills, then any axes the model
// returned that haven't been chosen yet as a row of selectable chips.
// Click an option → re-issue query with that refinement added.
// Click an active pill → remove it (re-issue without).
export default function RefinementChips({
  axes,
  active,
  onSelect,
  onClear,
  disabled = false,
}: RefinementChipsProps) {
  const activeEntries = Object.entries(active)
  const remainingAxes = axes.filter(a => !(a.key in active))

  if (activeEntries.length === 0 && remainingAxes.length === 0) return null

  return (
    <div className="space-y-2">
      {activeEntries.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-gray-500">Scoped to:</span>
          {activeEntries.map(([key, value]) => (
            <button
              key={key}
              type="button"
              onClick={() => onClear(key)}
              disabled={disabled}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Remove this refinement"
            >
              <span>{value}</span>
              <span aria-hidden="true">×</span>
            </button>
          ))}
        </div>
      )}

      {remainingAxes.map(axis => (
        <div key={axis.key} className="flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-gray-500">{axis.label}:</span>
          {axis.options.map(option => (
            <button
              key={option}
              type="button"
              onClick={() => onSelect(axis.key, option)}
              disabled={disabled}
              className="px-3 py-1 rounded-full bg-gray-50 text-gray-700 text-xs font-medium hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200 transition-colors border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {option}
            </button>
          ))}
        </div>
      ))}
    </div>
  )
}
